// src/admin/pages/Settings.jsx
import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, FormGroup, FormControlLabel, Switch,
  TextField, Button, Stack, Chip, Divider, Snackbar, Alert, Box, IconButton,
  Autocomplete,
} from '@mui/material';
import { prebuiltAppConfig } from '@mlc-ai/web-llm';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import GeminiModelField from '../components/GeminiModelField';
import { fetchAppConfig, saveAppConfig, fetchUsers, changeUserRole, fetchGeminiModels } from '../lib/adminApi';
import { canChangeRole } from '../lib/adminLogic';
import { DEFAULT_SLM_MODELS, DEFAULT_SLM_MODEL_ID, DEFAULT_THINKING_VERBS } from '../../slm/models';

const WEBLLM_MODEL_IDS = prebuiltAppConfig.model_list.map((m) => m.model_id).sort();

const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash-lite';

const FLAGS = [
  { key: 'localSlmEnabled', label: 'Mentor SLM locale (WebGPU)' },
  { key: 'registrationOpen', label: 'Registrazioni aperte' },
  { key: 'maintenanceMode', label: 'Modalità manutenzione' },
  { key: 'consoleLogsEnabled', label: 'Log in console del sito (default: disattivati)' },
];

const EMPTY_MODEL = { modelId: '', label: '', size: '', enabled: true };

function SlmModelRow({ model, isDefault, isLast, onToggleEnabled, onSetDefault, onEdit, onDelete }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Switch size="small" checked={!!model.enabled}
        onChange={(e) => onToggleEnabled(e.target.checked)}
        disabled={model.enabled && isLast}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>{model.label || model.modelId}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {model.modelId}{model.size ? ` · ${model.size}` : ''}
        </Typography>
      </Box>
      <Button size="small" variant={isDefault ? 'contained' : 'outlined'} disabled={!model.enabled}
        onClick={onSetDefault} sx={{ whiteSpace: 'nowrap', minWidth: 72 }}>
        {isDefault ? 'Default ✓' : 'Default'}
      </Button>
      <IconButton size="small" onClick={onEdit} title="Modifica">✏️</IconButton>
      <IconButton size="small" onClick={onDelete} disabled={isLast} title="Elimina">🗑</IconButton>
    </Box>
  );
}

function SlmModelForm({ value, onChange, onSave, onCancel, saveLabel = 'Salva' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '2px solid #1976d2', borderRadius: 2 }}>
      <Autocomplete
        freeSolo
        options={WEBLLM_MODEL_IDS}
        value={value.modelId}
        onInputChange={(_, v) => onChange({ ...value, modelId: v })}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Model ID (WebLLM)"
            helperText="Scegli dalla lista o digita un ID personalizzato" />
        )}
      />
      <TextField size="small" label="Nome visualizzato" value={value.label}
        onChange={(e) => onChange({ ...value, label: e.target.value })} />
      <TextField size="small" label="Dimensione (es. ~0.8 GB)" value={value.size}
        onChange={(e) => onChange({ ...value, size: e.target.value })} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={onSave}
          disabled={!value.modelId.trim() || !value.label.trim()}>
          {saveLabel}
        </Button>
        <Button size="small" onClick={onCancel}>Annulla</Button>
      </Box>
    </Box>
  );
}

export default function Settings() {
  const { currentUser } = useAuth();
  const [config, setConfig] = useState({ flags: {}, proCriteria: '' });
  const [cloud, setCloud] = useState({
    enabled: false,
    model: DEFAULT_GEMINI_MODEL,
    maxTokens: null,
    liteModel: DEFAULT_GEMINI_MODEL,
    liteMaxTokens: null,
  });
  const [slmList, setSlmList] = useState(DEFAULT_SLM_MODELS);
  const [slmDefault, setSlmDefault] = useState(DEFAULT_SLM_MODEL_ID);
  const [mentorVerbsText, setMentorVerbsText] = useState(DEFAULT_THINKING_VERBS.join('\n'));
  const [editingIdx, setEditingIdx] = useState(null); // index being edited
  const [editDraft, setEditDraft] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newDraft, setNewDraft] = useState(EMPTY_MODEL);
  const [users, setUsers] = useState([]);
  const [demote, setDemote] = useState(null);
  const [toast, setToast] = useState(null);
  const [geminiModels, setGeminiModels] = useState(null);
  const [geminiDefault, setGeminiDefault] = useState(DEFAULT_GEMINI_MODEL);

  useEffect(() => {
    fetchGeminiModels()
      .then((r) => { setGeminiModels(r.models); setGeminiDefault(r.default); })
      .catch(() => setGeminiModels(null));
  }, []);

  useEffect(() => {
    (async () => {
      const cfg = await fetchAppConfig();
      setConfig(cfg);
      setCloud(cfg.cloud && typeof cfg.cloud === 'object'
        ? {
            enabled: !!cfg.cloud.enabled,
            model: cfg.cloud.model || DEFAULT_GEMINI_MODEL,
            maxTokens: cfg.cloud.maxTokens ?? null,
            liteModel: cfg.cloud.liteModel || DEFAULT_GEMINI_MODEL,
            liteMaxTokens: cfg.cloud.liteMaxTokens ?? null,
          }
        : {
            enabled: false,
            model: DEFAULT_GEMINI_MODEL,
            maxTokens: null,
            liteModel: DEFAULT_GEMINI_MODEL,
            liteMaxTokens: null,
          });
      // Migrate old format { enabled, default } → new { models, default }
      const raw = cfg.slmModels;
      if (raw?.models?.length) {
        setSlmList(raw.models);
        setSlmDefault(raw.default || raw.models[0]?.modelId || DEFAULT_SLM_MODEL_ID);
      } else {
        setSlmList(DEFAULT_SLM_MODELS);
        setSlmDefault(DEFAULT_SLM_MODEL_ID);
      }
      const verbs = Array.isArray(cfg.mentorVerbs) ? cfg.mentorVerbs : [];
      setMentorVerbsText(verbs.length ? verbs.join('\n') : DEFAULT_THINKING_VERBS.join('\n'));
      setUsers(await fetchUsers());
    })();
  }, []);

  const setFlag = (key, value) => setConfig((c) => ({ ...c, flags: { ...c.flags, [key]: value } }));

  const enabledCount = slmList.filter((m) => m.enabled).length;

  const toggleEnabled = (idx, checked) => {
    const next = slmList.map((m, i) => i === idx ? { ...m, enabled: checked } : m);
    if (!next.some((m) => m.modelId === slmDefault && m.enabled)) {
      const first = next.find((m) => m.enabled);
      if (first) setSlmDefault(first.modelId);
    }
    setSlmList(next);
  };

  const startEdit = (idx) => { setEditingIdx(idx); setEditDraft({ ...slmList[idx] }); };
  const cancelEdit = () => { setEditingIdx(null); setEditDraft(null); };
  const saveEdit = () => {
    setSlmList((prev) => prev.map((m, i) => i === editingIdx ? editDraft : m));
    if (slmDefault === slmList[editingIdx].modelId) setSlmDefault(editDraft.modelId);
    cancelEdit();
  };

  const deleteModel = (idx) => {
    const deleted = slmList[idx];
    const next = slmList.filter((_, i) => i !== idx);
    setSlmList(next);
    if (slmDefault === deleted.modelId && next.length > 0) {
      const first = next.find((m) => m.enabled) || next[0];
      setSlmDefault(first.modelId);
    }
  };

  const saveAdd = () => {
    setSlmList((prev) => [...prev, newDraft]);
    setAddingNew(false);
    setNewDraft(EMPTY_MODEL);
  };

  const onSave = async () => {
    const mentorVerbs = mentorVerbsText.split('\n').map((v) => v.trim()).filter(Boolean);
    await saveAppConfig(currentUser, {
      flags: config.flags || {},
      proCriteria: config.proCriteria || '',
      slmModels: { models: slmList, default: slmDefault },
      cloud,
      mentorVerbs,
    });
    setToast({ severity: 'success', msg: 'Impostazioni salvate' });
  };

  const onDemoteConfirm = async () => {
    const check = canChangeRole({ targetId: demote.id, targetRole: 'Admin', newRole: 'Pro', admins: users.filter((u) => u.role === 'Admin'), actorUid: currentUser.uid });
    if (!check.ok) { setToast({ severity: 'error', msg: check.reason }); setDemote(null); return; }
    await changeUserRole(currentUser, demote, 'Pro');
    setDemote(null);
    setToast({ severity: 'success', msg: 'Amministratore rimosso' });
    setUsers(await fetchUsers());
  };

  const admins = users.filter((u) => u.role === 'Admin');

  return (
    <>
      <PageHeader title="Impostazioni" subtitle="Configurazione della piattaforma" />
      <Stack spacing={2}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Feature flag</Typography>
          <FormGroup>
            {FLAGS.map((f) => (
              <FormControlLabel key={f.key}
                control={<Switch checked={!!config.flags?.[f.key]} onChange={(e) => setFlag(f.key, e.target.checked)} />}
                label={f.label}
              />
            ))}
          </FormGroup>
          <TextField fullWidth multiline rows={3} margin="normal" label="Criteri per diventare Pro"
            value={config.proCriteria || ''} onChange={(e) => setConfig((c) => ({ ...c, proCriteria: e.target.value }))}
          />
          <Button variant="contained" onClick={onSave}>Salva</Button>
        </CardContent></Card>

        <Card><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Modelli SLM locali</Typography>
            <Button size="small" variant="outlined" onClick={() => { setAddingNew(true); setNewDraft(EMPTY_MODEL); }}
              disabled={addingNew}>
              + Aggiungi
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Modelli offerti agli utenti quando la quota cloud è esaurita. Almeno uno deve essere abilitato.
          </Typography>
          <Stack spacing={1}>
            {slmList.map((model, idx) => (
              editingIdx === idx ? (
                <SlmModelForm key={idx} value={editDraft} onChange={setEditDraft}
                  onSave={saveEdit} onCancel={cancelEdit} saveLabel="Aggiorna" />
              ) : (
                <SlmModelRow key={idx} model={model}
                  isDefault={slmDefault === model.modelId}
                  isLast={enabledCount === 1 && model.enabled}
                  onToggleEnabled={(v) => toggleEnabled(idx, v)}
                  onSetDefault={() => setSlmDefault(model.modelId)}
                  onEdit={() => startEdit(idx)}
                  onDelete={() => deleteModel(idx)}
                />
              )
            ))}
            {addingNew && (
              <SlmModelForm value={newDraft} onChange={setNewDraft}
                onSave={saveAdd} onCancel={() => setAddingNew(false)} saveLabel="Aggiungi" />
            )}
          </Stack>
          <Button variant="contained" sx={{ mt: 2 }} onClick={onSave}>Salva modelli</Button>
        </CardContent></Card>

        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Verbi del mentor</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Uno per riga — mostrati a rotazione mentre il mentor elabora una risposta.
            Vuoto = usa la lista predefinita.
          </Typography>
          <TextField fullWidth multiline rows={6} value={mentorVerbsText}
            onChange={(e) => setMentorVerbsText(e.target.value)}
          />
          <Button variant="contained" sx={{ mt: 2 }} onClick={onSave}>Salva verbi</Button>
        </CardContent></Card>

        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Cloud (Gemini)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Se abilitato, gli utenti vedono il tier cloud ☁ nel Mentor. Se disabilitato, solo il locale.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={cloud.enabled}
                onChange={(e) => setCloud((c) => ({ ...c, enabled: e.target.checked }))} />}
              label="Abilita tier cloud per gli utenti"
            />
          </FormGroup>
          <GeminiModelField label="Modello ☁ Cloud" value={cloud.model}
            models={geminiModels} defaultId={geminiDefault}
            onChange={(v) => setCloud((c) => ({ ...c, model: v }))} />
          <TextField fullWidth margin="normal" type="number" label="Limite token ☁ Cloud (vuoto = nessun limite)"
            value={cloud.maxTokens ?? ''}
            onChange={(e) => setCloud((c) => ({
              ...c, maxTokens: e.target.value === '' ? null : parseInt(e.target.value, 10),
            }))}
          />
          <GeminiModelField label="Modello 🪶 Lite" value={cloud.liteModel}
            models={geminiModels} defaultId={geminiDefault}
            onChange={(v) => setCloud((c) => ({ ...c, liteModel: v }))} />
          <TextField fullWidth margin="normal" type="number" label="Limite token 🪶 Lite (vuoto = nessun limite)"
            value={cloud.liteMaxTokens ?? ''}
            onChange={(e) => setCloud((c) => ({
              ...c, liteMaxTokens: e.target.value === '' ? null : parseInt(e.target.value, 10),
            }))}
          />
          <Button variant="contained" onClick={onSave}>Salva</Button>
        </CardContent></Card>

        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Amministratori</Typography>
          <Stack spacing={1}>
            {admins.map((a) => (
              <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label="Admin" color="error" size="small" />
                <Typography sx={{ flexGrow: 1 }}>{a.email}</Typography>
                <Button size="small" color="warning" disabled={admins.length <= 1} onClick={() => setDemote(a)}>Rimuovi</Button>
              </Box>
            ))}
          </Stack>
        </CardContent></Card>

        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Stato sistema</Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2">Build: {process.env.NODE_ENV}</Typography>
          <Typography variant="body2">Gemini: quota free ~20 generazioni/giorno (vedi backend).</Typography>
        </CardContent></Card>
      </Stack>

      <ConfirmDialog open={!!demote} title="Rimuovi amministratore" confirmLabel="Rimuovi" confirmColor="warning"
        message={`Rimuovere i privilegi di amministratore da ${demote?.email}? Verrà declassato a Pro.`}
        onConfirm={onDemoteConfirm} onClose={() => setDemote(null)}
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </>
  );
}
